import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, input, output, signal } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { AuthService } from '@app/modules/auth/services/auth.service'
import { UpdateWebhookPayload, Webhook } from '@app/v2/settings/enums/webhooks.model'
import { webhookHttpErrorMessage, WebhookService } from '@app/v2/settings/services/webhook.service'
import { Alert } from '@app/v2/shared/enums/alert.enum'
import { AlertService } from '@app/v2/shared/services/alert.service'
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import { finalize } from 'rxjs'

export type WebhookEventOption = {
  id: number
  name: string
  slug: string
}

@Component({
  selector: 'webhook-card',
  standalone: true,
  imports: [TranslateModule],
  templateUrl: './webhook-card.component.html',
  styleUrls: ['./webhook-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WebhookCardComponent {
  public readonly webhook = input.required<Webhook>()
  public readonly webhookUpdated = output<Webhook>()
  public readonly editRequested = output<Webhook>()
  public readonly deleteRequested = output<Webhook>()
  public readonly logsRequested = output<Webhook>()
  public readonly isSaving = signal(false)
  public readonly isActive = signal(true)
  public readonly displayLabel = computed(() => {
    const hook = this.webhook()
    return `${hook.name} / ${hook.event_slug}`
  })

  private readonly _destroyRef = inject(DestroyRef)
  private readonly _authService = inject(AuthService)
  private readonly _webhookService = inject(WebhookService)
  private readonly _translate = inject(TranslateService)
  private readonly _alertService = inject(AlertService)

  constructor() {
    effect(() => {
      this.isActive.set(this.webhook().active)
    })
  }

  /**
   * Toggles the webhook active state and persists it.
   * @param event {Event}
   */
  public onActiveToggle(event: Event): void {
    const input = event.target as HTMLInputElement
    const nextActive = input.checked
    const previousActive = this.isActive()

    this.isActive.set(nextActive)
    this._saveActiveState(nextActive, () => {
      this.isActive.set(previousActive)
      input.checked = previousActive
    })
  }

  /**
   * Opens the edit webhook modal.
   */
  public onEdit(): void {
    this.editRequested.emit(this.webhook())
  }

  /**
   * Requests deletion of this webhook.
   */
  public onDelete(): void {
    this.deleteRequested.emit(this.webhook())
  }

  /**
   * Opens webhook delivery logs.
   */
  public onLogs(): void {
    this.logsRequested.emit(this.webhook())
  }

  /**
   * Saves only the webhook active flag.
   * @param nextActive {boolean}
   * @param onError {() => void}
   */
  private _saveActiveState(nextActive: boolean, onError?: () => void): void {
    if (this.isSaving()) return

    const organizationId = this._authService.currentWorkspace?.id
    if (!organizationId) return

    const hook = this.webhook()
    const payload: UpdateWebhookPayload = {
      event_id: hook.event_id,
      event_slug: hook.event_slug,
      name: hook.name,
      webhook_url: hook.webhook_url,
      triggers: hook.triggers ?? [],
      active: nextActive
    }

    this.isSaving.set(true)
    this._webhookService
      .updateWebhook(hook.id, payload)
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        finalize(() => this.isSaving.set(false))
      )
      .subscribe({
        next: (updatedWebhook) => this.webhookUpdated.emit(updatedWebhook),
        error: (error) => {
          this._alertService.alert(Alert.ERROR, webhookHttpErrorMessage(error, this._translate.instant('v2.orgSettings.webhooks.updateWebhookError')))
          onError?.()
        }
      })
  }
}
